import { useEffect, useState } from "react";
import { apiClient, formatApiError, uploadFile, resolveUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Upload, X, Loader2, Eye } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost, btnDanger } from "@/components/admin/ui";

const empty = { title: "", category: "custom_furniture", description: "", image_url: "" };
const cats = CATEGORIES.filter((c) => c.value !== "all");

export default function GalleryAdminPage() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState(null);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const fetchItems = () => {
        setLoading(true);
        apiClient.get(`/gallery${filter !== "all" ? `?category=${filter}` : ""}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
    };
    useEffect(fetchItems, [filter]); // eslint-disable-line

    const del = async (id) => {
        if (!window.confirm("Delete this image?")) return;
        await apiClient.delete(`/gallery/${id}`);
        toast.success("Deleted");
        fetchItems();
    };

    return (
        <>
            <PageHeader
                title="Gallery"
                subtitle="Manage individual gallery images."
                action={<button onClick={() => setEditing({ ...empty })} data-testid="add-gallery-btn" className={btnPrimary}><Plus size={16} /> Add image</button>}
            />

            <div className="flex flex-wrap gap-2 mb-6">
                {CATEGORIES.map((c) => (
                    <button key={c.value} onClick={() => setFilter(c.value)} className={`px-3 py-1.5 text-xs transition-colors ${filter === c.value ? "bg-walnut text-cream" : "border border-stone-border dark:border-stone-700 text-stone-muted dark:text-stone-300 hover:bg-sand dark:hover:bg-stone-800"}`}>
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? <Loader2 className="animate-spin text-walnut" /> : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((g) => (
                        <Card key={g.id} className="overflow-hidden group" data-testid={`gallery-admin-${g.id}`}>
                            <div className="aspect-square bg-sand dark:bg-stone-800 relative">
                                <img src={resolveUrl(g.image_url)} alt={g.title} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                    <button onClick={() => setPreview(g)} className="bg-cream/90 text-walnut p-2"><Eye size={16} /></button>
                                    <button onClick={() => setEditing({ ...g })} className="bg-cream/90 text-walnut p-2"><Edit3 size={16} /></button>
                                    <button onClick={() => del(g.id)} data-testid={`gallery-delete-${g.id}`} className="bg-red-600 text-white p-2"><Trash2 size={16} /></button>
                                </div>
                            </div>
                            <div className="p-3">
                                <div className="text-sm font-medium truncate">{g.title}</div>
                                <div className="text-[10px] uppercase tracking-widest text-walnut-light mt-1">{g.category}</div>
                            </div>
                        </Card>
                    ))}
                    {!items.length && <div className="col-span-full text-stone-muted py-12">No images.</div>}
                </div>
            )}

            {editing && <GalleryModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchItems(); }} />}

            {preview && (
                <div onClick={() => setPreview(null)} className="fixed inset-0 z-50 bg-black/85 p-4 flex items-center justify-center">
                    <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full">
                        <img src={resolveUrl(preview.image_url)} alt={preview.title} className="w-full max-h-[80vh] object-contain" />
                        <div className="text-cream mt-4">
                            <div className="font-serif text-2xl">{preview.title}</div>
                            {preview.description && <p className="text-stone-300 mt-1 text-sm">{preview.description}</p>}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function GalleryModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const isEdit = !!initial.id;

    const handleUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const r = await uploadFile(f);
            setForm({ ...form, image_url: r.url });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) await apiClient.patch(`/gallery/${form.id}`, { title: form.title, category: form.category, description: form.description, image_url: form.image_url });
            else await apiClient.post("/gallery", { title: form.title, category: form.category, description: form.description, image_url: form.image_url });
            toast.success(isEdit ? "Updated" : "Added");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-auto">
            <form onSubmit={submit} className="bg-cream dark:bg-[#171311] w-full max-w-xl p-6 md:p-8" data-testid="gallery-form">
                <div className="flex justify-between items-start mb-6">
                    <h2 className="font-serif text-2xl">{isEdit ? "Edit image" : "Add image"}</h2>
                    <button type="button" onClick={onClose}><X /></button>
                </div>
                <div className="space-y-4">
                    <Field label="Title" required><input data-testid="gallery-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
                    <Field label="Category" required>
                        <select data-testid="gallery-category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                            {cats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Image">
                        <div className="flex gap-3 items-start">
                            {form.image_url && <img src={resolveUrl(form.image_url)} alt="" className="w-24 h-24 object-cover bg-sand" />}
                            <div className="flex-1 space-y-2">
                                <input data-testid="gallery-image-url" placeholder="https://..." className={inputCls} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
                                <label className={btnGhost + " cursor-pointer"}>
                                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload file
                                    <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                                </label>
                            </div>
                        </div>
                    </Field>
                    <Field label="Description"><textarea rows={3} className={inputCls + " resize-none"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                </div>
                <div className="flex gap-3 mt-6">
                    <button disabled={saving} className={btnPrimary} data-testid="gallery-save">{isEdit ? "Save" : "Add"}</button>
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                </div>
            </form>
        </div>
    );
}
